import { useEffect, useRef, type RefObject } from 'react';

/** Number keys 1-9 select options on the live question card. The listener sits on document (a
 * non-focused container never receives keydown) but only acts while focus is inside the card's own
 * [data-commenda-ai-surface] ancestor — with the side panel open over the app, keys pressed while the
 * user works the underlying page do nothing. Keys are also ignored while focus is in any input, so
 * typing in the composer or the write-in never selects an option. The card moves focus to itself
 * when it goes live, which is what puts focus inside the surface in the first place. */
export function useQuestionCardHotkeys(
  cardRef: RefObject<HTMLElement | null>,
  isEnabled: boolean,
  onNumberKey: (numberKey: number) => void
) {
  // Latest callback via a ref so the document listener never re-attaches per render.
  const onNumberKeyRef = useRef(onNumberKey);
  useEffect(() => {
    onNumberKeyRef.current = onNumberKey;
  }, [onNumberKey]);

  useEffect(() => {
    if (!isEnabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const surface = cardRef.current?.closest('[data-commenda-ai-surface]');
      if (!surface) return;
      const active = document.activeElement;
      if (!active || !surface.contains(active)) return;
      if (
        active instanceof HTMLElement &&
        (active.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName))
      )
        return;
      const numberKey = Number(event.key);
      if (!Number.isInteger(numberKey) || numberKey < 1 || numberKey > 9)
        return;
      event.preventDefault();
      onNumberKeyRef.current(numberKey);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cardRef, isEnabled]);
}
